import React, { Fragment, useState, useReducer, useEffect } from "react"
import BlockWrapper from "@yumgmbh/gatsby-theme-yum-components/src/components/technical/BlockWrapper"
import Richtext from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Richtext"
import Link from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Link"
import { useTranslation } from "react-i18next"
import { useStaticQuery, graphql } from "gatsby"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleXmark } from "@fortawesome/pro-light-svg-icons"
import axios from "axios"

const NewsletterBlock = ({ block }) => {
  // Query newsletter datasource
  const newsletterData = useStaticQuery(graphql`
    query NewsletterQuery {
      allStoryblokDatasourceEntry(
        filter: { data_source: { eq: "newsletter" } }
      ) {
        edges {
          node {
            value
            name
          }
        }
      }
    }
  `)

  const newsletters = newsletterData.allStoryblokDatasourceEntry.edges

  const initialState = { email: "", listIds: [], attributes: {} }

  const requiredFields = ["email", "listIds"]

  const attributeFields = ["Vorname", "Nachname", "Titel"]

  const formReducer = (state, event) => {
    switch (event.type) {
      case "update":
        if (attributeFields.includes(event.name)) {
          return {
            ...state,
            attributes: {
              ...state.attributes,
              [event.name]: event.value,
            },
          }
        } else if (
          requiredFields.includes(event.name) ||
          requiredFields.includes(event.name.split("[")[0])
        ) {
          return {
            ...state,
            [event.name]: event.value,
            ...state.attributes,
          }
        } else {
          return null
        }

      case "reset":
        return initialState
      default:
        return initialState
    }
  }

  useEffect(() => {
    return () => {
      setFormSubmitted(false)
      setFormErrors({})
      setNotificationState(null)
      setFormData({ type: "reset" })
      setShowForm(false)
    }
  }, [])

  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useReducer(formReducer, initialState)
  const [formErrors, setFormErrors] = useState({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [notificationState, setNotificationState] = useState(null)

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
      type: "update",
    })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setFormErrors(validate(formData))
    setFormSubmitted(true)
  }

  const resetForm = () => {
    setFormSubmitted(false)
    setFormErrors({})
    setNotificationState(null)
    setShowForm(true)
  }

  const { t } = useTranslation()
  const { i18n } = useTranslation()

  // set titles based on language
  let titles

  if (i18n.language === "de") {
    titles = [
      {
        name: "Herr",
        value: "herr",
      },
      {
        name: "Frau",
        value: "frau",
      },
      {
        name: "Divers",
        value: "divers",
      },
    ]
  } else if (i18n.language === "en") {
    titles = [
      {
        name: "Mr",
        value: "mr",
      },
      {
        name: "Mrs",
        value: "mrs",
      },
    ]
  }

  const hasNewsletterChecked = (object) => {
    for (let key in object) {
      if (key.startsWith("listIds") && object[key] === "on") {
        return true
      }
    }
    return false
  }

  const validate = (values) => {
    // console.log("values", values)
    const errors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i
    if (!values.email) {
      errors.email = `${t("newsletter.errors.email")}`
    } else if (!emailRegex.test(values.email)) {
      errors.email = `${t("newsletter.errors.email")}`
    }

    if (!hasNewsletterChecked(values)) {
      errors.listIds = `${t("newsletter.errors.newsletter")}`
    }

    return errors
  }

  const sanitizeFormDataByList = (data) => {
    let result = []

    attributeFields.forEach(function (key) {
      delete data[key]
    })

    for (let key in data) {
      if (key.startsWith("listIds") && data[key] === "on") {
        let id = Number(key.slice(8, -1))
        let obj = { ...data, listId: id }
        delete obj[key]

        for (let key2 in data) {
          if (key2.startsWith("listIds")) {
            delete obj[key2]
          }
        }
        result.push(obj)
      }
    }
    return result
  }

  const subscribe = (data) => {
    sanitizeFormDataByList(data).forEach((sanitizedFormData) => {
      axios
        .post(process.env.INXMAIL_API_URL, sanitizedFormData)
        .then((res) => {
          res.data.result === "PENDING_SUBSCRIPTION"
            ? setNotificationState("SUCCESS")
            : res.data.result === "DUPLICATE_SUBSCRIPTION"
            ? setNotificationState("WARNING")
            : setNotificationState("ERROR")
          setFormData({ type: "reset" })
        })
        .catch((err) => {
          console.error(err)
          setNotificationState("ERROR")
        })
    })
  }

  const NOTIFICATION_TYPES = {
    SUCCESS: (
      <div className="p-4 mt-4 border-l-4 rounded-md bg-secondary-700 border-secondary-500">
        <div className="flex justify-between">
          <div className="ml-3">
            <p className="text-sm font-medium text-white">
              Sie erhalten in Kürze eine E-Mail. Bitte klicken sie auf den
              mitgelieferten Link, um die Anmeldung zu bestätigen.
            </p>
          </div>
          <FontAwesomeIcon
            onClick={resetForm}
            icon={faCircleXmark}
            size="xl"
            className="text-white cursor-pointer"
          />
        </div>
      </div>
    ),
    ERROR: (
      <div className="p-4 mt-4 bg-red-700 border-l-4 border-red-500 rounded-md">
        <div className="flex justify-between">
          <div className="ml-3">
            <p className="text-sm font-medium text-white">
              Etwas ist schief gelaufen, bitte versuchen Sie es zu einem
              späteren Zeitpunkt nochmal.
            </p>
          </div>
          <FontAwesomeIcon
            onClick={resetForm}
            icon={faCircleXmark}
            size="xl"
            className="text-white cursor-pointer"
          />
        </div>
      </div>
    ),
    WARNING: (
      <div className="p-4 mt-4 bg-yellow-500 border-l-4 border-yellow-300 rounded-md">
        <div className="flex justify-between">
          <div className="ml-3">
            <p className="text-sm font-medium text-black">
              Sie haben sich bereits zum ausgewählten Newsletter angemeldet.
            </p>
          </div>
          <FontAwesomeIcon
            onClick={resetForm}
            icon={faCircleXmark}
            size="xl"
            className="text-black cursor-pointer"
          />
        </div>
      </div>
    ),
  }

  const NEWSLETTER_DESCRIPTIONS = {
    11: "Informiert wöchentlich über neue Nachrichten und aktuelle Entwicklungen in der Welt des Einkaufs und der Logistik.",
    118: "Informiert über Neuigkeiten zur innovativen öffentlichen Beschaffung, KOINNO-Seminaren, Veranstaltungen und Praxisbeispielen.",
    125: "Informiert monatlich über Stellenanzeigen aus Einkauf, Supply Chain und Logistik, News aus dem BME und Events für deine Karriere.",
    136: "Informiert und unterstützt bei Beschaffungs- und Supply-Chain-Aktivitäten auf den globalen Beschaffungsmärkten (in englischer Sprache).",
    242: "Informiert über Studienergebnisse, Veröffentlichungen, Sonderkonditionen für Teilnehmende und viele weitere Angebote.",
    269: "Informiert zweiwöchentlich über die aktuellsten Weiterbildungsmöglichkeiten für Ihre Einkaufs- und Logistikorganisation.",
  }

  const Notification = ({ notificationState }) => {
    return <>{NOTIFICATION_TYPES[notificationState]}</>
  }

  useEffect(() => {
    // console.log("formErrors: ", formErrors)
    if (Object.keys(formErrors).length === 0 && formSubmitted) {
      subscribe(formData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors])

  return (
    <BlockWrapper
      block={block}
      blockWidth="content"
      showHeadline="true"
      className="bg-gray-100"
    >
      <div className="mt-8">
        <Richtext text={block.text} />
        {!notificationState ? (
          <button
            onClick={() => setShowForm(true)}
            className={`btn btn-primary mt-4 ${showForm ? "hidden" : ""}`}
          >
            {`${t("newsletter.button")}`}
          </button>
        ) : (
          <Notification notificationState={notificationState} />
        )}
      </div>
      <div
        className={`p-4 mt-4 bg-white rounded-xl ${showForm ? "" : "hidden"}`}
      >
        {!notificationState && (
          <form className="text-gray-500" onSubmit={handleSubmit}>
            <div className="flex flex-row flex-wrap -mx-grid">
              <div className="w-full mb-4 md:w-1/2 px-grid">
                <label className="block" htmlFor="email">
                  {`${t("newsletter.email")}`}
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                  onChange={handleChange}
                  type="text"
                  id="email"
                  name="email"
                />
                <span className="text-sm">{`${t("newsletter.required")}`}</span>
                <p className="text-red-500">{formErrors.email}</p>
              </div>
              <div className="w-full mb-4 px-grid">
                <div className="md:w-1/2">
                  <label className="block" htmlFor="newsletter">
                    {`${t("newsletter.selection")}`}
                  </label>
                  <div className="mt-2 space-y-3">
                    {newsletters.map((newsletter, index) => {
                      if (block.newsletters.includes(newsletter.node.value)) {
                        // console.dir(newsletter)
                        return (
                          <div
                            key={index}
                            className="relative flex items-start"
                          >
                            <div className="flex items-center h-6">
                              <input
                                id={`listIds[${newsletter.node.value}]`}
                                onChange={handleChange}
                                name={`listIds[${newsletter.node.value}]`}
                                type="checkbox"
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-600"
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor={`listIds[${newsletter.node.value}]`}
                                className="font-medium text-gray-900"
                              >
                                {newsletter.node.name}
                              </label>
                              <p className="text-gray-500">
                                {NEWSLETTER_DESCRIPTIONS[newsletter.node.value]}
                              </p>
                            </div>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>

                  <span className="text-sm">{`${t(
                    "newsletter.required"
                  )}`}</span>
                  <p className="text-red-500">{formErrors.newsletter}</p>
                  <Link link={block.link} className="text-primary">{`${t(
                    "newsletter.overview"
                  )}`}</Link>
                </div>
              </div>
              <div className="w-full px-grid">
                {titles.map((title, index) => (
                  <Fragment key={index}>
                    <input
                      type="radio"
                      name="Titel"
                      value={title.value}
                      onChange={handleChange}
                    />
                    <label className="ml-2 mr-4" htmlFor="herr">
                      {title.name}
                    </label>
                  </Fragment>
                ))}
              </div>
              <div className="w-full px-grid md:w-1/2">
                <label htmlFor="Vorname">{`${t(
                  "newsletter.firstName"
                )}`}</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                  onChange={handleChange}
                  type="text"
                  id="Vorname"
                  name="Vorname"
                />
              </div>
              <div className="w-full px-grid md:w-1/2">
                <label htmlFor="Nachname">{`${t("newsletter.name")}`}</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                  onChange={handleChange}
                  type="text"
                  id="Nachname"
                  name="Nachname"
                />
              </div>
            </div>
            <button type="submit" className="mt-4 btn btn-primary">
              {`${t("newsletter.button")}`}
            </button>
          </form>
        )}

        <Richtext
          className="mt-4 text-sm text-gray-500"
          text={block.disclaimer}
        />
      </div>
    </BlockWrapper>
  )
}

export default NewsletterBlock
